<template>
	<div class="chat-log h-100 d-flex flex-column overflow-hidden">
		<div class="d-flex flex-fill justify-content-center align-items-center" v-if="loading > 0">
			<b-icon icon="circle-fill" animation="throb" font-scale="2"></b-icon>
		</div>
		<div v-show="loading <= 0" ref="alertLog" class="flex-fill overflow-auto dashboardAlertLog" @scroll="onScroll($event)"></div>
		<div class="chatLogScrollToBottom" v-show="historyMode">
			<b-button @click="bottomOut()"><b-icon icon="arrow-down"></b-icon></b-button>
		</div>
		<div class="alert-log-footer p-2 d-flex align-items-center">
			<b-button size="sm" :variant="stopped ? 'success' : 'danger'" class="mr-2" :title="stopped ? 'Start playing alerts' : 'Stop playing alerts'" @click="toggleStopped()">
				<b-icon :icon="stopped ? 'play-fill' : 'stop-fill'"></b-icon>
				<span class="alert-log-btn-label">{{stopped ? 'Start' : 'Stop'}}</span>
			</b-button>
			<b-button size="sm" variant="secondary" class="mr-2" :title="paused ? 'Resume the alert queue' : 'Pause the alert queue'" @click="togglePause()" :disabled="stopped">
				<b-icon :icon="paused ? 'play-fill' : 'pause-fill'"></b-icon>
				<span class="alert-log-btn-label">{{paused ? 'Resume' : 'Pause'}}</span>
			</b-button>
			<b-button size="sm" variant="secondary" title="Skip the alert that is playing" @click="skip()">
				<b-icon icon="skip-end-fill"></b-icon>
				<span class="alert-log-btn-label">Skip</span>
			</b-button>
			<b-button size="sm" :variant="user?.highlighted_message ? 'danger' : 'secondary'" class="ml-auto mr-2" title="Stop showing the highlighted message" :disabled="!user?.highlighted_message" @click="hideHighlightedMessage()"><b-icon icon="x"/>Hide</b-button>
			<message-type-filter
				mode="alerts"
				variant="secondary"
				dropup
				show-tests
				:gift-thresholds="giftThresholds"
				:currency="user && user.currency_code"
				v-model="hiddenTypes"
				@clear="clearLog()"
				@test="playTestAlert($event.type, $event.data)"
			></message-type-filter>
		</div>
		<message-details-overlay
			:message="selectedMessage"
			:user="user"
			:serviceIcons="serviceIcons"
			@close="selectedMessage = null"
			@select="highlightMessage($event)"
			@replay="replayAlert($event)"
		/>
	</div>
</template>

<script>
import util from '@/util';
import giftColorThresholds from '@/GiftThresholds';
import MessageDetailsOverlay from '@/components/MessageDetailsOverlay.vue';
import MessageTypeFilter from '@/components/MessageTypeFilter.vue';
import { loadHiddenMessageTypes } from '@/logMessageFilter';

export default {
	components: { MessageDetailsOverlay, MessageTypeFilter },
	props: {
		serviceIcons: Object,
	},
	data() { return {
		// Event types to leave out of the log; owned here because the filter control sits in
		// this component's footer. Loaded up front so the first render is already filtered.
		hiddenTypes: loadHiddenMessageTypes('alerts'),
		loading: 0,
		paused: false,
		stopped: false,
		historyMode: false,
		user: null,
		giftTypes: {},
		giftThresholds: [],
		selectedMessage: null,
	}},
	created() {
		this.scrollPos = 0;
		this.prevHeight = 0;
		this.prevScrollHeight = 0;
		this.loadingMore = false;
		this.noMoreHistory = false;
	},
	watch: {
		// Reload from the start so the log shows a full page of the types that are still visible.
		hiddenTypes() { this.reloadAlerts(); },
	},
	async mounted() {
		this._injectGiftColors();
		try {
			this.loading++;
			this.startScrollAnimation();
			this.user = await this.$api.get('userData');
			const [alerts, giftTypesResult, giftSettings, giftThresholds] = await Promise.all([
				this.$api.get('alerts'),
				this.$api.get('userGiftTypes'),
				this.$api.get('widgetSettings/gift'),
				this.$api.get('giftThresholds'),
			]);
			this.stopped = !!giftSettings?.stopped;
			this.giftThresholds = giftThresholds || [];
			this.loadGiftTypes(giftTypesResult.results);
			this.renderAlerts(alerts);
			this.subId = this.$socket.subscribe(this.user.widget_hash, {
				userWidgetAlert: msg => {
					if(msg.sent_at) return;
					if(msg.type == 'tower') return;
					if(!this.historyMode) this.appendAlert(msg.message, 'queued', msg.id);
				},
				alertLogCleared: () => this.reloadAlerts(),
				userWidgetAlertStatus: msg => {
					const found = document.querySelector(`[data-alert-id="${CSS.escape(msg.alertId)}"]`);
					if(found) this._setStatusBadge(found, msg.status);
				},
				widgetSettingsFieldChange: msg => {
					if(msg.widgetType === 'gift' && msg.field === 'stopped') {
						this.stopped = !!msg.value;
					}
				},
				messageHighlighted: msg => {
					const log = this.$refs.alertLog;
					if(log) for(const h of Array.from(log.getElementsByClassName('highlighted'))) h.classList.remove('highlighted');
					this.user.highlighted_message = msg;
					// The chat log renders the same message (same id) in its own DOM, so a
					// bare getElementById would just grab whichever log happens to come first.
					log?.querySelector(`[id="${CSS.escape(msg.id)}"]`)?.classList.add('highlighted');
				},
				messageUnhighlighted: () => {
					const log = this.$refs.alertLog;
					if(log) for(const h of Array.from(log.getElementsByClassName('highlighted'))) h.classList.remove('highlighted');
					this.user.highlighted_message = null;
				},
			});
		} catch(e) {
			console.error(e);
		} finally {
			this.loading--;
		}
	},
	beforeDestroy() {
		this.stopScrollAnimation();
		this.subId && this.$socket.unsubscribe(this.subId);
	},
	methods: {
		_injectGiftColors() {
			if(document.getElementById('alert-log-gift-colors')) return;
			const style = document.createElement('style');
			style.id = 'alert-log-gift-colors';
			style.textContent = giftColorThresholds.map(t => `
				.dashboardAlertLog > .message.gift.gift-${t.value}:not(.highlighted) {
					background-color: ${t.color};
					color: ${util.getContrastingTextColor(t.color)};
				}
				.dashboardAlertLog > .message.gift.gift-${t.value}:not(.highlighted):hover {
					background-color: color-mix(in srgb, ${t.color} 90%, #000);
				}
			`).join('');
			document.head.appendChild(style);
		},
		loadGiftTypes(giftTypes) {
			this.giftTypes = {};
			for(const giftType of giftTypes) {
				if(!(giftType.service_id in this.giftTypes))
					this.giftTypes[giftType.service_id] = {};
				this.giftTypes[giftType.service_id][giftType.gift_type] = giftType;
			}
		},
		_setStatusBadge(elem, status) {
			let badge = elem.querySelector('.alert-status-badge');
			if(!status || status === 'queued') {
				badge?.remove();
				return;
			}
			if(!badge) {
				badge = document.createElement('span');
				badge.className = 'alert-status-badge';
				elem.appendChild(badge);
			}
			const labels = { playing: '▶ playing', played: '✓ played', error: '⚠ error', paused: '⏸ paused', skipped: '⏭ skipped', ignored: '⊘ ignored' };
			badge.textContent = labels[status] || status;
			badge.dataset.status = status;
		},
		renderAlerts(alerts) {
			const log = this.$refs.alertLog;
			if(!log) return;
			log.innerHTML = '';
			for(const alert of alerts || [])
				this.appendAlert(alert.message, alert.status, alert.id);
			this.historyMode = false;
			log.scrollTop = log.scrollHeight;
		},
		async reloadAlerts() {
			let alerts = null;
			try {
				this.loading++;
				alerts = await this.$api.get('alerts');
			} catch(e) {
				console.error(e);
			} finally {
				this.loading--;
			}
			if(!alerts) return;
			// the log is hidden while loading; wait for it back before measuring its scroll height
			await this.$nextTick();
			this.renderAlerts(alerts);
		},
		async clearLog() {
			const confirmed = await this.$bvModal.msgBoxConfirm('This permanently deletes your alert log, including anything still queued to play.', {
				title: 'Clear alert log',
				size: 'sm',
				buttonSize: 'sm',
				okVariant: 'danger',
				okTitle: 'Clear',
				footerClass: 'p-2',
				centered: true,
			});
			if(!confirmed) return;
			try {
				await this.$api.delete('alerts');
				await this.reloadAlerts();
			} catch(e) {
				console.error(e);
				this.$bvToast.toast('Could not clear the alert log: '+(e.error||String(e)), {
					title: 'Error',
					variant: 'danger',
					solid: true,
				});
			}
		},
		createAlertElem(msg, status, id) {
			const elem = util.createChatLogMessage(msg, {
				serviceIcons: this.serviceIcons,
				giftColorThresholds,
				user: this.user,
				giftTypes: this.giftTypes,
			}, {
				onclick: prepared => { this.selectedMessage = prepared; },
				onmenuclick: prepared => this.highlightMessage(prepared),
			}, this.$refs.alertLog);
			if(!elem) return; // folded into an existing combo — see createChatLogMessage
			if(id) elem.dataset.alertId = id;
			this._setStatusBadge(elem, status);
			return elem;
		},
		appendAlert(msg, status, id) {
			if(!this.$refs.alertLog) return;
			if(this.hiddenTypes.includes(util.resolveEventType(msg))) return;
			const elem = this.createAlertElem(msg, status, id);
			if(elem) this.$refs.alertLog.appendChild(elem);
		},
		prependAlerts(alerts) {
			if(!this.$refs.alertLog) return;
			const fragment = document.createDocumentFragment();
			for(const alert of alerts) {
				try {
					fragment.appendChild(this.createAlertElem(alert.message, alert.status, alert.id));
				} catch(e) {
					console.error(e);
				}
			}
			this.$refs.alertLog.prepend(fragment);
		},
		startScrollAnimation() {
			if(this._animationId) return;
			const frame = () => {
				this._animationId = window.requestAnimationFrame(frame);
				if(!this.$refs.alertLog || this.historyMode) return;
				const el = this.$refs.alertLog;
				const diff = el.scrollHeight - el.offsetHeight - el.scrollTop;
				if(diff > 400) {
					el.scrollTop += diff;
				} else {
					el.scrollTop += Math.round(diff / 5) + 1;
				}
			};
			this._animationId = window.requestAnimationFrame(frame);
		},
		stopScrollAnimation() {
			if(this._animationId) {
				window.cancelAnimationFrame(this._animationId);
				delete this._animationId;
			}
		},
		onScroll(event) {
			const el = event.target;
			const scrollBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
			if(this.historyMode) {
				if(scrollBottom < 1) {
					this.historyMode = false;
				} else if(el.scrollTop < 1 && !this.loadingMore && !this.noMoreHistory) {
					const beforeId = el.firstChild?.dataset.alertId;
					if(beforeId) {
						this.loadingMore = true;
						this.$api.get('alerts', { limit: 100, beforeId }).then(alerts => {
							if(!alerts.length) {
								this.noMoreHistory = true;
								return;
							}
							const prevScrollHeight = el.scrollHeight;
							this.prependAlerts(alerts);
							el.scrollTop = el.scrollHeight - prevScrollHeight;
						}).finally(() => {
							this.loadingMore = false;
						});
					}
				}
			} else {
				const scrolledPx = el.scrollTop - this.scrollPos;
				const resizedPx = el.clientHeight - this.prevHeight;
				const innerPx = el.scrollHeight - this.prevScrollHeight;
				if(innerPx === 0 && resizedPx === 0 && scrolledPx < 0)
					this.historyMode = true;
			}
			this.scrollPos = el.scrollTop;
			this.prevHeight = el.clientHeight;
			this.prevScrollHeight = el.scrollHeight;
		},
		bottomOut() {
			this.historyMode = false;
			if(this.$refs.alertLog)
				this.$refs.alertLog.scrollTop = this.$refs.alertLog.scrollHeight;
		},
		highlightMessage(msg) {
			// Not document.getElementById: the chat log renders this same message (same id)
			// in its own DOM, so a bare lookup could grab that copy instead of this one.
			const target = this.$refs.alertLog?.querySelector(`[id="${CSS.escape(msg.id)}"]`);
			target?.classList.add('loading');
			const is_current = String(msg.serviceId) == String(this.user?.highlighted_message?.serviceId) && msg.token == this.user?.highlighted_message?.token;
			this.$api.post('selectMessage', is_current ? null : msg).then(() => {
				const highlighted = this.$refs.alertLog?.getElementsByClassName('highlighted');
				if(highlighted) for(const h of highlighted) h.classList.remove('highlighted');
				if(is_current) {
					this.user.highlighted_message = null;
				} else {
					target?.classList.add('highlighted');
					this.user.highlighted_message = msg;
				}
			}).catch(err => {
				this.$bvToast.toast('Could not select message: ' + (err.error || err.toString()), {
					title: 'Error',
					variant: 'danger',
					solid: true,
				});
			}).finally(() => {
				target?.classList.remove('loading');
			});
			this.$broadcastChannel?.postMessage({
				type: 'highlightedMessage',
				data: is_current ? null : JSON.parse(JSON.stringify(msg)),
			});
		},
		hideHighlightedMessage() {
			this.$api.post('selectMessage', null).then(() => {
				const highlighted = this.$refs.alertLog?.getElementsByClassName('highlighted');
				if(highlighted) for(const h of highlighted) h.classList.remove('highlighted');
				this.user.highlighted_message = null;
			}).catch(err => {
				this.$bvToast.toast('Could not hide message: ' + (err.error || err.toString()), {
					title: 'Error',
					variant: 'danger',
					solid: true,
				});
			});
			this.$broadcastChannel?.postMessage({
				type: 'highlightedMessage',
				data: null,
			});
		},
		replayAlert(msg) {
			this.$api.post('alerts/replay', { serviceId: msg.serviceId, token: msg.token });
		},
		async toggleStopped() {
			try {
				await this.$api.post('widgetSettings/gift/stopped', { value: !this.stopped });
				// stopped state updates reactively via widgetSettingsFieldChange socket event
			} catch(e) {
				console.error(e);
			}
		},
		async togglePause() {
			try {
				if(this.paused) {
					await this.$api.post('alerts/queue/resume');
					this.paused = false;
				} else {
					await this.$api.post('alerts/queue/pause');
					this.paused = true;
				}
			} catch(e) {
				console.error(e);
			}
		},
		async skip() {
			try {
				await this.$api.post('alerts/skip');
			} catch(e) {
				console.error(e);
			}
		},
		async playTestAlert(type, data) {
			try {
				await this.$api.post('alerts/test', { type, data });
			} catch(e) {
				console.error(e);
				this.$bvToast.toast('Could not send test alert: ' + (e.error || String(e)), {
					title: 'Error', variant: 'danger', solid: true,
				});
			}
		},
	},
};
</script>

<style lang="scss">
@import '../plugins/variable.scss';

/* Differentiate the controls footer with a slightly lighter background instead of a border. */
.alert-log-footer {
	background-color: rgba(255, 255, 255, 0.05);
	// Sized by the panel, not the window, so the controls drop their labels when this panel is
	// narrow even if the window is wide. Containment also makes the footer a stacking context,
	// hence the z-index: without it the scroll-to-bottom button (z-index 2 in the log's context)
	// would paint over an open dropup menu.
	container-type: inline-size;
	position: relative;
	z-index: 3;
}
.alert-log-footer .alert-log-btn-label { margin-left: .2rem; }
// Five controls fit here rather than the chat log's three, so the words have to go sooner.
// The buttons keep their icons, and their tooltips.
@container (max-width: 360px) {
	.alert-log-footer .alert-log-btn-label { display: none; }
}

.chat-log {
	> .dashboardAlertLog {
		word-break: break-word;

		> .message {
			cursor: pointer;
			padding: 2px 4px;
			position: relative;
			img { max-width: 100%; }
			// One or two gift images float beside the text; the moment a 3rd shows up,
			// :has() switches the whole block to its own centered row below the text
			// instead — no JS-picked class needed. Each further :has(:nth-child(N))
			// rule then shrinks that row a notch more: they're listed smallest-N-first,
			// and since e.g. a 10-image block matches all of them simultaneously, the
			// largest N present wins the cascade (equal specificity, later wins). Same
			// technique GiftWidget.js uses for its own combo stack.
			.chatImages { float: right; }
			.chatImages > .chatImage { max-width: 80px; max-height: 80px; }
			.chatImages:has(.chatImage:nth-child(3)) {
				float: none;
				clear: both;
				display: flex;
				flex-wrap: wrap;
				justify-content: center;
				align-items: center;
				gap: 2px;
				margin-top: 2px;
			}
			.chatImages:has(.chatImage:nth-child(3)) > .chatImage { max-width: 48px; max-height: 48px; }
			.chatImages:has(.chatImage:nth-child(4)) > .chatImage { max-width: 40px; max-height: 40px; }
			.chatImages:has(.chatImage:nth-child(5)) > .chatImage { max-width: 36px; max-height: 36px; }
			.chatImages:has(.chatImage:nth-child(6)) > .chatImage { max-width: 32px; max-height: 32px; }
			.chatImages:has(.chatImage:nth-child(7)) > .chatImage { max-width: 28px; max-height: 28px; }
			.chatImages:has(.chatImage:nth-child(8)) > .chatImage { max-width: 26px; max-height: 26px; }
			.chatImages:has(.chatImage:nth-child(9)) > .chatImage { max-width: 24px; max-height: 24px; }
			.chatImages:has(.chatImage:nth-child(10)) > .chatImage { max-width: 22px; max-height: 22px; }
			.chatImages > .chatImages-overflow {
				font-weight: bold;
				opacity: 0.85;
				padding: 0 2px;
			}
			.clearfix { clear: both; }
			&:hover { background: rgba(255, 255, 255, .1); }
			a {
				color: $vaporwave-green;
				text-decoration: underline;
				word-break: break-all;
				&:hover { color: lighten($vaporwave-green, 10%); }
			}
			&.highlighted a { color: darken($vaporwave-green, 35%); }
			// On colored alert backgrounds the default green link is low-contrast
			// (green-on-green for memberships); use the alert's own text color, still underlined.
			&.gift a, &.gift a:hover,
			&.product a, &.product a:hover,
			&.subscription a, &.subscription a:hover,
			&.subGiftReceive a, &.subGiftReceive a:hover { color: inherit; }
			&.gift, &.product {
				background: $gold;
				color: $gold-text;
				margin: 4px;
				border-radius: 4px;
				padding: 4px;
				&:hover { background: darken($gold, 5%); }
			}
			&.subscription, &.subGiftReceive {
				background: #0f9d58;
				color: white;
				margin: 4px;
				border-radius: 4px;
				padding: 4px;
				&:hover { background: #0a8043; }
			}
			&.highlighted {
				background: $vaporwave-white;
				color: $dark;
				&:hover { background: darken($vaporwave-white, 5%); }
			}
			> .avatar, > .service {
				width: 24px;
				height: 24px;
				object-fit: cover;
				overflow: hidden;
			}
			> .avatar {
				border-radius: 12px;
			}
			> .name:after { content: ' ' }
			&.chat > .name:after { content: ': ' }
			&.chat.gift > .name:after { content: ' ' }
			&.chat.product > .name:after { content: ' ' }
			> .content {
				display: inline;
				> img {
					max-width: 80px;
					max-height: 80px;
					&.emote { max-width: 32px; max-height: 32px; }
				}
			}
			&.self {
				color: fade-out($vaporwave-white, .4);
				&.highlighted { color: $dark; }
			}
			&.owner {
				color: $vaporwave-green;
				&.highlighted { color: $dark; }
			}
			> div.menubtn {
				display: none;
				position: absolute;
				font-size: 15px;
				right: 0; top: 0; bottom: 0;
				background: transparent;
				line-height: 100%;
				align-items: center;
				padding: 4px 24px;
				text-align: center;
				color: white;
				&:after { content: '⊕ Show'; }
				backdrop-filter: blur(3px);
				z-index: 1;
			}
			&.highlighted > div.menubtn {
				color: $dark;
				&:after { content: '⊖ Hide'; }
			}
			&:hover > div.menubtn { display: inline-flex; }
			&:hover > div.menubtn:hover { background: rgba(255, 255, 255, .1); }
			> .alert-status-badge {
				position: absolute;
				right: 4px;
				top: 50%;
				transform: translateY(-50%);
				font-size: 10px;
				padding: 1px 5px;
				border-radius: 3px;
				pointer-events: none;
				background: rgba(0,0,0,.5);
				color: #ccc;
				&[data-status="playing"] { background: rgba(52,152,219,.7); color: #fff; }
				&[data-status="played"]  { background: rgba(39,174, 96,.6); color: #fff; }
				&[data-status="error"]   { background: rgba(231,76, 60,.7); color: #fff; }
				&[data-status="paused"]  { background: rgba(243,156, 18,.7); color: #fff; }
				&[data-status="skipped"] { background: rgba(149,165,166,.6); color: #fff; }
			&[data-status="ignored"] { background: rgba(149,165,166,.35); color: #ccc; }
			}
		}
	}

	// Takes no space of its own directly under the scroll area and lifts its button back up
	// into it, so it stays pinned to the bottom of the log whatever the footer's height is.
	> .chatLogScrollToBottom {
		position: relative;
		z-index: 2;
		height: 0;
		text-align: center;
		pointer-events: none;
		> button { transform: translateY(calc(-100% - .4rem)); pointer-events: auto; }
	}
}
</style>
